import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * UI version — "v1" is the original mobile UI (header on every page,
 * dense Accounts hero card). "v2" is the Robinhood-inspired redesign:
 * no TopAppBar except on Profile, sticky account selector, chart fills
 * available space, header actions (Search / AI / LiveChat / Feedback /
 * Notifications) move onto the Profile page as tappable rows.
 *
 * Toggle is exposed in the TopAppBar mobile overflow menu (so V1 users
 * can opt back in if they prefer the old layout) AND on the Profile
 * page (V2's primary opt-out path, since V2 hides the TopAppBar
 * everywhere except /profile).
 *
 * Persisted to localStorage as "whb-ui-version" so the preference
 * survives refreshes. Default is "v2" — V2 is now the shipped UI;
 * V1 is kept available as a rollback for users who prefer it.
 * Persist middleware only writes on first state mutation, so users
 * who never touched the toggle (no localStorage entry) follow this
 * default on next visit without needing a cache bust.
 */
export type UiVersion = "v1" | "v2";

interface UiVersionState {
  uiVersion: UiVersion;
  setUiVersion: (v: UiVersion) => void;
  toggle: () => void;
}

export const useUiVersionStore = create<UiVersionState>()(
  persist(
    (set) => ({
      uiVersion: "v2",
      setUiVersion: (v) => set({ uiVersion: v }),
      toggle: () => set((s) => ({ uiVersion: s.uiVersion === "v1" ? "v2" : "v1" }))
    }),
    {
      name: "whb-ui-version",
      storage: createJSONStorage(() => localStorage)
    }
  )
);
